import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  TextField,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  FormControl,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warehouse as WarehouseIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { createWarehouse, getAllWarehouses, deleteWarehouse, resetWarehouses } from '../services/inventoryService';
import { db } from '../config/firebase';
import { Warehouse } from '../utils/types';
import { doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

// التحقق من توفر خدمة Firestore
const isFirestoreAvailable = (): boolean => {
  if (!db) {
    console.error('خطأ: خدمة Firestore غير متوفرة');
    return false;
  }
  return true;
};

const WarehousePage: React.FC = () => {
  // حالة المخازن
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  // حالة النموذج
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [forceDelete, setForceDelete] = useState(false);
  const [currentWarehouse, setCurrentWarehouse] = useState<Warehouse>({
    id: '',
    name: '',
    location: '',
    createdAt: new Date(),
    updatedAt: new Date()
  });
  const [isEditMode, setIsEditMode] = useState(false);

  // حالة الإشعارات
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  // تحميل بيانات المخازن
  const loadWarehouses = async () => {
    try {
      console.log('Loading warehouses...');
      setLoading(true);
      setError(null);
      const fetchedWarehouses = await getAllWarehouses();
      console.log('Fetched warehouses:', fetchedWarehouses);
      setWarehouses(fetchedWarehouses);
      setLoading(false);
      console.log('Warehouses loaded successfully');
    } catch (err) {
      console.error('Error loading warehouses:', err);
      setError('حدث خطأ أثناء تحميل بيانات المخازن');
      setLoading(false);
    }
  };

  // تحميل البيانات عند التحميل الأولي
  useEffect(() => {
    loadWarehouses();
  }, []);

  // فتح نموذج إضافة مخزن جديد
  const handleAddWarehouse = () => {
    setCurrentWarehouse({
      id: '',
      name: '',
      location: '',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    setIsEditMode(false);
    setOpenAddDialog(true);
  };

  // فتح نموذج تعديل مخزن
  const handleEditWarehouse = (warehouse: Warehouse) => {
    setCurrentWarehouse(warehouse);
    setIsEditMode(true);
    setOpenAddDialog(true);
  };

  // فتح حوار تأكيد الحذف
  const handleDeleteConfirm = (warehouse: Warehouse) => {
    setCurrentWarehouse(warehouse);
    setForceDelete(false);
    setOpenDeleteDialog(true);
  };

  // تغيير قيمة في النموذج
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentWarehouse(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // حفظ المخزن (إضافة أو تعديل)
  const handleSaveWarehouse = async () => {
    try {
      if (!currentWarehouse.name) {
        setSnackbar({
          open: true,
          message: 'يرجى إدخال اسم المخزن',
          severity: 'error',
        });
        return;
      }

      if (isEditMode) {
        // تعديل مخزن
        await updateWarehouse(currentWarehouse.id, currentWarehouse.name, currentWarehouse.location || '');
        setSnackbar({
          open: true,
          message: 'تم تحديث المخزن بنجاح',
          severity: 'success',
        });
      } else {
        // إضافة مخزن جديد
        await createWarehouse(currentWarehouse.name, currentWarehouse.location);
        setSnackbar({
          open: true,
          message: 'تم إضافة المخزن بنجاح',
          severity: 'success',
        });
      }

      setOpenAddDialog(false);
      loadWarehouses();
    } catch (err) {
      console.error('Error saving warehouse:', err);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ المخزن',
        severity: 'error',
      });
    }
  };

  // حذف المخزن
  const handleDeleteWarehouse = async () => {
    if (!currentWarehouse.id) return;

    try {
      console.log(`Attempting to delete warehouse: ${currentWarehouse.id}, force: ${forceDelete}`);
      console.log('Current warehouse:', currentWarehouse);

      // حذف المخزن من قاعدة البيانات
      console.log('Calling deleteWarehouse function...');
      await deleteWarehouse(currentWarehouse.id, forceDelete);
      console.log('deleteWarehouse function completed successfully');

      setSnackbar({
        open: true,
        message: 'تم حذف المخزن بنجاح',
        severity: 'success',
      });

      setOpenDeleteDialog(false);
      console.log('Loading warehouses after deletion...');
      await loadWarehouses();
      console.log('Warehouses loaded successfully');
    } catch (err: any) {
      console.error('Error deleting warehouse:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      setSnackbar({
        open: true,
        message: err.message || 'حدث خطأ أثناء حذف المخزن',
        severity: 'error',
      });
    }
  };



  // تعديل مخزن
  const updateWarehouse = async (id: string, name: string, location: string): Promise<void> => {
    try {
      if (!isFirestoreAvailable()) {
        throw new Error('خدمة Firestore غير متوفرة');
      }

      const warehouseRef = doc(db!, 'warehouses', id);
      await updateDoc(warehouseRef, {
        name,
        location,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating warehouse:', error);
      throw error;
    }
  };



  // إغلاق الإشعارات
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg">
      <Box my={4}>
        <Grid container spacing={2} alignItems="center" mb={4}>
          <Grid item xs>
            <Typography variant="h4" component="h1" gutterBottom>
              <WarehouseIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              إدارة المخازن
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddWarehouse}
              sx={{ mr: 1 }}
            >
              إضافة مخزن جديد
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => navigate('/admin/admin-tools')}
            >
              أدوات إدارة المخازن
            </Button>
          </Grid>
        </Grid>

        {/* عرض المخازن */}
        <Paper elevation={3} sx={{ p: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={5}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : warehouses.length === 0 ? (
            <Alert severity="info">لا توجد مخازن مضافة. قم بإضافة مخزن جديد.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>اسم المخزن</TableCell>
                    <TableCell>الموقع</TableCell>
                    <TableCell>تاريخ الإنشاء</TableCell>
                    <TableCell align="center">الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {warehouses.map((warehouse) => (
                    <TableRow key={warehouse.id}>
                      <TableCell>{warehouse.name}</TableCell>
                      <TableCell>{warehouse.location || "—"}</TableCell>
                      <TableCell>
                        {warehouse.createdAt instanceof Date
                          ? warehouse.createdAt.toLocaleDateString('ar-SA')
                          : new Date(warehouse.createdAt).toLocaleDateString('ar-SA')}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="تعديل المخزن">
                          <IconButton
                            color="primary"
                            aria-label="تعديل المخزن"
                            onClick={() => handleEditWarehouse(warehouse)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف المخزن">
                          <IconButton
                            color="error"
                            aria-label="حذف المخزن"
                            onClick={() => handleDeleteConfirm(warehouse)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* نموذج إضافة/تعديل مخزن */}
        <Dialog
          open={openAddDialog}
          onClose={() => setOpenAddDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {isEditMode ? 'تعديل مخزن' : 'إضافة مخزن جديد'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="اسم المخزن"
                  name="name"
                  value={currentWarehouse.name}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: <WarehouseIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="موقع المخزن"
                  name="location"
                  value={currentWarehouse.location || ''}
                  onChange={handleInputChange}
                  margin="normal"
                  InputProps={{
                    startAdornment: <LocationIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAddDialog(false)} color="inherit">
              إلغاء
            </Button>
            <Button onClick={handleSaveWarehouse} color="primary" variant="contained">
              حفظ
            </Button>
          </DialogActions>
        </Dialog>

        {/* حوار تأكيد الحذف */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>
            حذف مخزن
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography>
                هل أنت متأكد من حذف المخزن "{currentWarehouse.name}"؟ قد يؤثر ذلك على المنتجات المرتبطة بهذا المخزن.
              </Typography>
              <FormControl sx={{ mt: 2, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 1 }}>حذف قسري:</Typography>
                <Switch
                  checked={forceDelete}
                  onChange={(e) => setForceDelete(e.target.checked)}
                  color="warning"
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (استخدم هذا الخيار لحذف المخزن حتى لو كان مرتبطاً بمنتجات أو فروع)
                </Typography>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)} color="inherit">
              إلغاء
            </Button>
            <Button onClick={handleDeleteWarehouse} color="error" variant="contained">
              حذف
            </Button>
          </DialogActions>
        </Dialog>

        {/* الإشعارات */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default WarehousePage;